// ANGEL FLIGHT (color) — CityBackdrop: the war-torn dusk horizon.
//
// Fills the empty far edge of the world so it reads as a city on fire at dusk,
// not a flat fog wall. Two pure-procedural, no-asset pieces:
//
//   1) SKYLINE silhouette — a wide curved band of quads sitting just inside
//      camera.farM and behind fog.far (so THREE.Fog tints it into the haze).
//      Textured with a procedural CanvasTexture: a dark building band with
//      randomized rectangles + a few antenna spires + soft orange fire-glow
//      gradients behind. It counter-scrolls VERY slowly (RENDER.skyline.
//      parallaxFactor of the world scroll), wrapping, for far parallax depth.
//
//   2) SMOKE COLUMNS — a handful of vertical stacks of additive billboard
//      Sprites rising from points across the mid/far battlefield (dark grey-
//      brown crown, warm ember base), each topped by a small orange fire sprite
//      and a faint flickering low-intensity PointLight at the base. Columns
//      slide toward the near edge with the world scroll and recycle to the far
//      edge (like props), so the city is visibly burning.
//
// All tunables come from RENDER.skyline{} / RENDER.smoke{}. No per-frame
// allocation in update(); all textures/geometries/materials/lights disposed in
// dispose(). Make its own small glow texture (does NOT import from Juice3D).

import * as THREE from 'three';
import { RENDER } from '../config/render';

/** A single smoke billboard within a column. `up` is its metres-above-base; it
 *  rises, grows, and fades as it climbs, then recycles to the base. */
interface SmokePuff {
  sprite: THREE.Sprite;
  mat: THREE.SpriteMaterial;
  /** Height above the column base (metres); wraps back to ~0 at the top. */
  up: number;
  /** Phase offset (0..1) so the stack's puffs are staggered up the column. */
  phase: number;
  /** Per-puff base scale (metres) — bigger toward the crown. */
  baseScale: number;
  /** 0 = warm ember (base), 1 = cold grey-brown (crown) — color lerp target. */
  coldness: number;
}

/** One burning point: a stack of smoke puffs + a fire sprite + a flicker light.
 *  `worldZ` is its sim-depth; it slides toward the near edge and recycles. */
interface SmokeColumn {
  group: THREE.Group;
  puffs: SmokePuff[];
  fire: THREE.Sprite;
  fireMat: THREE.SpriteMaterial;
  light: THREE.PointLight;
  worldX: number;
  worldZ: number;
  /** The column's full height (metres) the puffs cycle through. */
  height: number;
  /** Independent flicker phase so columns don't pulse in lockstep. */
  flicker: number;
}

export class CityBackdrop {
  private readonly scene: THREE.Scene;
  private readonly group = new THREE.Group();

  // --- Skyline ---
  private skylineMat!: THREE.MeshBasicMaterial;
  private skylineTex!: THREE.CanvasTexture;
  private skylineGeo!: THREE.BufferGeometry;
  private skylineMesh!: THREE.Mesh;
  private skylineOffset = 0; // accumulated counter-scroll (texture U offset, wraps)

  // --- Smoke ---
  private readonly smokeGroup = new THREE.Group();
  private smokeTex!: THREE.CanvasTexture;
  private fireTex!: THREE.CanvasTexture;
  private readonly columns: SmokeColumn[] = [];
  private readonly disposables: Array<{ dispose(): void }> = [];

  // Scratch color (no per-frame alloc).
  private readonly scratchColor = new THREE.Color();
  private readonly emberColor = new THREE.Color(0xff7a32); // warm base
  private readonly ashColor = new THREE.Color(0x3a342f); // cold grey-brown crown

  /** Per-mission fire-glow strength (the night theme burns brighter). Defaults to
   *  the base skyline fireGlow when not overridden. */
  private readonly fireGlow: number;

  constructor(scene: THREE.Scene, fireGlow: number = RENDER.skyline.fireGlow) {
    this.scene = scene;
    this.fireGlow = fireGlow;
    this.scene.add(this.group);
    this.group.add(this.smokeGroup);
    this.buildSkyline();
    this.buildSmoke();
  }

  // ---------------------------------------------------------------------------
  // SKYLINE
  // ---------------------------------------------------------------------------

  /** Paint the procedural skyline-silhouette CanvasTexture: a dark building band
   *  with randomized rectangles of varying heights/widths + a few antenna/spire
   *  spikes + 2-3 soft orange fire-glow gradients behind the buildings. Low-
   *  contrast, soft, dark — it's meant to sit in haze. The texture repeats
   *  horizontally so the band can counter-scroll and wrap. */
  private buildSkylineTexture(): THREE.CanvasTexture {
    const W = 1024;
    const H = 256;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const g = c.getContext('2d')!;

    // Transparent above the band; the buildings/glows sit at the bottom so the
    // dusk sky shows through above the rooftops.
    g.clearRect(0, 0, W, H);

    // 1) Soft orange fire-glow blooms behind the buildings (drawn first, low).
    const glowCount = 3;
    for (let i = 0; i < glowCount; i++) {
      const gx = ((i + 0.5) / glowCount) * W + (Math.random() - 0.5) * 160;
      // Centre the blooms just below the ROOFLINE (canvas y ≈ 0.46-0.60 H) so
      // they halo the silhouette edge — lower placements sat in the part of the
      // band that's hidden behind the ground horizon in-frame.
      const gy = H * (0.45 + Math.random() * 0.14);
      const r = 90 + Math.random() * 110;
      const grad = g.createRadialGradient(gx, gy, 0, gx, gy, r);
      const a = this.fireGlow;
      grad.addColorStop(0, `rgba(255,150,60,${a})`);
      grad.addColorStop(0.45, `rgba(220,90,40,${a * 0.5})`);
      grad.addColorStop(1, 'rgba(180,60,30,0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, W, H);
    }

    // 2) The dark building band — randomized rectangles marching across, drawn in
    //    two depth layers (a hazier far row behind a darker near row) for a touch
    //    of internal depth. Deterministic-ish via plain Math.random (one-off).
    const drawRow = (color: string, baseTop: number, jitter: number, gap: number): void => {
      g.fillStyle = color;
      let x = -20;
      while (x < W + 20) {
        const bw = 22 + Math.random() * 46;
        const top = baseTop + (Math.random() - 0.5) * jitter;
        g.fillRect(x, top, bw, H - top);
        // Occasional antenna/spire spike on a rooftop.
        if (Math.random() < 0.22) {
          const sx = x + bw * (0.3 + Math.random() * 0.4);
          const sh = 10 + Math.random() * 26;
          g.fillRect(sx - 1.5, top - sh, 3, sh);
        }
        x += bw + gap + Math.random() * gap;
      }
    };
    // Far, hazier row (lighter so it recedes), then the near darker row. The
    // values are PRE-HAZED warm darks (the material has fog OFF — scene fog at
    // the band's depth blended the near-black originals ~2/3 toward the bright
    // amber haze, leaving the "silhouette" LIGHTER than the sky: inverted).
    // These stay clearly darker than every theme's horizon glow.
    drawRow('rgba(96,66,48,0.92)', H * 0.46, 44, 6);
    drawRow('rgba(56,36,30,1.0)', H * 0.60, 56, 5);

    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    // Repeat horizontally a couple of times so the silhouette has variety across
    // the very wide band and the wrap seam is hard to spot.
    tex.repeat.set(3, 1);
    tex.needsUpdate = true;
    return tex;
  }

  /** A wide CURVED band (an open partial cylinder) wrapping the far horizon,
   *  textured with the skyline silhouette. Curving it toward the camera means it
   *  always faces the eye and fills the frame as the camera banks/sways — no flat
   *  side-panels presenting edge-on (the bug a flat 3-quad band had). The texture
   *  wraps around the arc and counter-scrolls very slowly for parallax. */
  private buildSkyline(): void {
    const s = RENDER.skyline;
    this.skylineTex = this.buildSkylineTexture();

    this.skylineMat = new THREE.MeshBasicMaterial({
      map: this.skylineTex,
      transparent: true,
      depthWrite: false, // it's a far backdrop; never occlude or z-fight
      // Fog OFF: at ~110m the scene fog blended the dark buildings ~2/3 toward
      // the bright amber haze — the silhouette rendered LIGHTER than the sky
      // behind it (value inversion). The haze is baked into the texture instead.
      fog: false,
      side: THREE.BackSide, // we view the cylinder from INSIDE
    });

    // An open-ended partial cylinder: radius = distanceM, arc wide enough to fill
    // the FOV plus banking margin. We see it from inside (BackSide), centred on
    // the camera's forward (-Z) direction. arcLen ≈ widthM sets the angular span.
    const radius = s.distanceM;
    const arcLen = s.widthM;
    const thetaLength = arcLen / radius; // radians of arc the band subtends
    // Centre the arc on -Z (straight ahead). CylinderGeometry places theta = 0
    // on +Z (x = r·sinθ, z = r·cosθ), so the arc midpoint must sit at θ = π.
    // (The old -π/2 start centred the arc on -X — the band's slanted EDGE cut
    // across the frame while the camera looked at the arc's side.)
    const thetaStart = Math.PI - thetaLength / 2;
    this.skylineGeo = new THREE.CylinderGeometry(
      radius,
      radius,
      s.heightM,
      48, // radial segments across the arc — smooth curve, cheap
      1,
      true, // open-ended (no caps)
      thetaStart,
      thetaLength,
    );

    this.skylineMesh = new THREE.Mesh(this.skylineGeo, this.skylineMat);
    // Lift so the jagged ROOFLINE (texture v ≈ 0.40-0.55) lands inside the thin
    // visible sky sliver. The camera pitch puts the frame top at ≈ y 22m and the
    // ground horizon at ≈ y 2m (both at the band's depth); a 0.18·height lift
    // puts the roofline at y ≈ 6-15m — silhouette edge + sky above it in frame.
    // (The old 0.32 lift pushed every rooftop above the frame top, so only the
    // band's solid lower wall showed: a flat haze with no city shape.)
    this.skylineMesh.position.set(0, s.heightM * 0.18, 0);
    this.skylineMesh.renderOrder = -10; // draw early, behind everything
    this.group.add(this.skylineMesh);
  }

  // ---------------------------------------------------------------------------
  // SMOKE
  // ---------------------------------------------------------------------------

  /** A soft radial-gradient puff texture (opaque-ish core → transparent edge) for
   *  billowing smoke. White so the per-sprite material color tints it. */
  private buildSmokeTexture(): THREE.CanvasTexture {
    const size = 64;
    const cv = document.createElement('canvas');
    cv.width = cv.height = size;
    const g = cv.getContext('2d')!;
    const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0, 'rgba(255,255,255,0.9)');
    grad.addColorStop(0.5, 'rgba(255,255,255,0.35)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  /** A hotter, tighter glow texture for the fire sprite at each column base. */
  private buildFireTexture(): THREE.CanvasTexture {
    const size = 64;
    const cv = document.createElement('canvas');
    cv.width = cv.height = size;
    const g = cv.getContext('2d')!;
    const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0, 'rgba(255,240,200,1)');
    grad.addColorStop(0.35, 'rgba(255,150,50,0.8)');
    grad.addColorStop(1, 'rgba(255,90,30,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  private buildSmoke(): void {
    const sm = RENDER.smoke;
    this.smokeTex = this.buildSmokeTexture();
    this.fireTex = this.buildFireTexture();
    this.disposables.push(this.smokeTex, this.fireTex);

    // Spread columns across the mid/far battlefield: depth between just past the
    // prop band and the skyline; lateral biased to the sides so they frame the
    // action rather than block the firing lane.
    const nearZ = RENDER.world.propSpawnZ - 10; // ~60m out
    const farZ = RENDER.skyline.distanceM - 30; // well in front of the skyline
    const span = farZ - nearZ;

    for (let i = 0; i < sm.columnCount; i++) {
      const t = (i + 0.5) / sm.columnCount;
      const worldZ = nearZ + t * span + (Math.random() - 0.5) * (span / sm.columnCount);
      this.columns.push(this.buildColumn(this.pickLateralX(), worldZ));
    }
  }

  /** A lateral spawn x for a smoke column: spread across the ground width but
   *  ALWAYS at least minLateralM out of the centre firing lane, so a column can
   *  never read as a frame-filling wall in front of the gun. */
  private pickLateralX(): number {
    const sm = RENDER.smoke;
    const sideHalf = RENDER.world.groundSizeM * 0.4;
    let x = (Math.random() * 2 - 1) * sideHalf;
    if (Math.abs(x) < sm.minLateralM) x = x >= 0 ? sm.minLateralM : -sm.minLateralM;
    return x;
  }

  /** Build one smoke column at sim-depth `worldZ` (placed via Three.Z = -worldZ). */
  private buildColumn(worldX: number, worldZ: number): SmokeColumn {
    const sm = RENDER.smoke;
    const grp = new THREE.Group();
    // Tall plume so it reads as a RISING column, not a ground blob.
    const height = 24 + Math.random() * 14;
    // A slight constant lean so the plumes look wind-blown (war-torn feel). Each
    // column leans a touch differently; applied as horizontal drift up the stack.
    const lean = (Math.random() * 2 - 1) * 0.18;

    const puffs: SmokePuff[] = [];
    for (let i = 0; i < sm.spritesPerColumn; i++) {
      const phase = i / sm.spritesPerColumn;
      const coldness = Math.min(1, phase * 1.15); // base = warm, crown = ashen
      const mat = new THREE.SpriteMaterial({
        map: this.smokeTex,
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        depthTest: true,
        blending: THREE.NormalBlending, // smoke is absorptive, not additive
        fog: true,
        rotation: Math.random() * Math.PI * 2,
      });
      const sprite = new THREE.Sprite(mat);
      const baseScale = 3.4 + phase * 5.6; // billows wider as it rises
      sprite.scale.setScalar(baseScale);
      sprite.userData.lean = lean; // px-of-lean per metre-up (read in update)
      grp.add(sprite);
      puffs.push({ sprite, mat, up: phase * height, phase, baseScale, coldness });
    }

    // The fire sprite at the base (additive so it blooms through the grade).
    const fireMat = new THREE.SpriteMaterial({
      map: this.fireTex,
      color: 0xffb347,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      fog: true,
    });
    const fire = new THREE.Sprite(fireMat);
    fire.scale.setScalar(5.2);
    fire.position.y = 1.6;
    grp.add(fire);

    // A faint, low-intensity flickering point light at the base.
    const light = new THREE.PointLight(0xff7a30, sm.baseFireLight, 30, 2);
    light.position.set(0, 1.8, 0);
    grp.add(light);

    const t = { x: worldX, y: 0, z: -worldZ };
    grp.position.set(t.x, t.y, t.z);
    this.smokeGroup.add(grp);

    return {
      group: grp,
      puffs,
      fire,
      fireMat,
      light,
      worldX,
      worldZ,
      height,
      flicker: Math.random() * Math.PI * 2,
    };
  }

  // ---------------------------------------------------------------------------
  // UPDATE
  // ---------------------------------------------------------------------------

  /** Advance the backdrop. `dt` is millis; `scrollDist` is the world ground
   *  scroll this frame (metres) — the SAME value ColorWorld slides props by — so
   *  the smoke recycles in lockstep with the battlefield. */
  update(dt: number, scrollDist: number): void {
    const dtSec = dt / 1000;

    // --- Skyline: very slow counter-scroll for far parallax (wraps via Repeat). ---
    this.skylineOffset =
      (this.skylineOffset +
        (scrollDist * RENDER.skyline.parallaxFactor) / RENDER.skyline.widthM) %
      1;
    // map.offset.x shifts the texture; multiply by repeat.x is implicit in wrap.
    this.skylineTex.offset.x = this.skylineOffset;

    // --- Smoke columns: rise/grow/fade puffs + flicker light, slide + recycle. ---
    // Columns recycle at RENDER.smoke.recycleZ — well in FRONT of the near
    // frustum (the camera sits at world z ≈ −9; the old prop-line recycle slid
    // 30m plumes through the camera plane as full-frame black slabs) — and they
    // FADE over the last nearFadeM metres so the respawn (behind the far fog)
    // never pops in-frame.
    const sm = RENDER.smoke;
    const recycleZ = sm.recycleZ;
    const respawnZ = RENDER.skyline.distanceM - 28;

    for (let ci = 0; ci < this.columns.length; ci++) {
      const col = this.columns[ci]!;

      // Slide toward the near edge with the world (+ a little extra drift).
      col.worldZ -= scrollDist + sm.driftMps * dtSec;
      if (col.worldZ <= recycleZ) {
        // Recycle to the far edge (behind the fog band) at a fresh lateral spot.
        const nx = this.pickLateralX();
        col.worldX = nx;
        col.worldZ = respawnZ;
        col.group.position.x = nx;
      }
      col.group.position.z = -col.worldZ;

      // Near-edge fade: 1 at recycleZ + nearFadeM, 0 at recycleZ.
      const nearFade = Math.min(1, Math.max(0, (col.worldZ - recycleZ) / sm.nearFadeM));

      // Flicker the base fire light + sprite (cheap sin sum, no alloc).
      col.flicker += dtSec * 9;
      const flick = 0.7 + 0.3 * Math.sin(col.flicker) * Math.sin(col.flicker * 2.3 + 1.0);
      col.light.intensity = sm.baseFireLight * flick * nearFade;
      col.fireMat.opacity = (0.75 + 0.25 * flick) * nearFade;
      col.fire.scale.setScalar(4.6 + 1.0 * flick);

      // Rise + grow + fade each puff up the column, recycling at the crown.
      for (let pi = 0; pi < col.puffs.length; pi++) {
        const p = col.puffs[pi]!;
        p.up += sm.riseSpeedMps * dtSec;
        if (p.up >= col.height) p.up -= col.height; // wrap to the base
        const k = p.up / col.height; // 0 base → 1 crown
        p.sprite.position.y = 1.0 + p.up;
        // Lean with height so the plume reads wind-blown (war-torn). userData.lean
        // is a constant per column; multiply by metres-up for the x offset.
        p.sprite.position.x = (p.sprite.userData.lean as number) * p.up;
        // Grow as it climbs; widen near the crown.
        p.sprite.scale.setScalar(p.baseScale * (0.65 + k * 1.25));
        // Fade in off the base, hold through the mid, fade at the crown. A bit
        // heavier than a pure hump so the column has visible body against the
        // amber ground/haze (still capped low for mobile fill-rate). nearFade
        // dissolves the whole column before it can reach the near frame.
        const fade = Math.sin(Math.min(1, k) * Math.PI); // 0→1→0
        p.mat.opacity = 0.55 * fade * nearFade;
        // Warm ember at the base → ashen grey-brown at the crown.
        this.scratchColor.copy(this.emberColor).lerp(this.ashColor, Math.min(1, k * 1.3));
        p.mat.color.copy(this.scratchColor);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // DISPOSE
  // ---------------------------------------------------------------------------

  dispose(): void {
    // Skyline.
    if (this.skylineMesh) this.group.remove(this.skylineMesh);
    this.skylineGeo?.dispose();
    this.skylineMat?.dispose();
    this.skylineTex?.dispose();

    // Smoke.
    for (const col of this.columns) {
      for (const p of col.puffs) p.mat.dispose();
      col.fireMat.dispose();
      col.light.dispose();
      this.smokeGroup.remove(col.group);
    }
    this.columns.length = 0;
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;

    this.group.remove(this.smokeGroup);
    this.scene.remove(this.group);
  }
}
